import React from "react";
import { LogoUploadButton, LookColourFields } from "./LogoAndColours.js";
import { uploadColorsFrom, type Look, type LookColors } from "./wizard.js";

/**
 * What a film from an AI summary is dressed in.
 *
 * A separate choice because the summary cannot supply it: logos and colours
 * read off search results are not reliable, and the company is often not the
 * workspace's own. An upload is for this film only and leaves the kit alone.
 */
export const LookChoice: React.FC<{
  look: Look | null;
  kitColors: LookColors;
  onChange: (look: Look) => void;
}> = ({ look, kitColors, onChange }) => {
  const upload = look?.kind === "upload" ? look : null;

  const card = (selected: boolean) =>
    `flex flex-col gap-0.5 rounded-[8px] border px-3.5 py-2.5 text-left transition-colors ${
      selected ? "border-ember-400 bg-bay-900" : "border-[var(--edge)] hover:border-ink-400"
    }`;

  return (
    <div className="flex flex-col gap-2" role="radiogroup" aria-label="Logo and colours">
      <h3 className="text-[12.5px] font-semibold text-ink-100">Logo and colours</h3>

      <div className={card(upload !== null)}>
        <button
          type="button"
          role="radio"
          aria-checked={upload !== null}
          className="flex flex-col gap-0.5 text-left"
          onClick={() =>
            !upload && onChange({ kind: "upload", logoKey: null, logoUrl: null, colors: uploadColorsFrom(kitColors) })
          }
        >
          <span className="text-[12.5px] font-semibold text-ink-100">Upload the logo and pick colours</span>
          <span className="text-[11.5px] text-ink-300">For this video only. Your brand kit isn't changed.</span>
        </button>
        {upload ? (
          <div className="mt-2 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-[var(--edge)] bg-bay-850">
                {upload.logoUrl ? (
                  <img src={upload.logoUrl} alt="" className="h-full w-full object-contain p-1.5" />
                ) : (
                  <span className="text-[10.5px] text-ink-400">none</span>
                )}
              </div>
              <LogoUploadButton
                hasLogo={upload.logoKey !== null}
                primary={upload.logoKey === null}
                onUploaded={(stored) => onChange({ ...upload, logoKey: stored.logoKey, logoUrl: stored.logoUrl })}
              />
            </div>
            <LookColourFields colors={upload.colors} onChange={(colors) => onChange({ ...upload, colors })} />
          </div>
        ) : null}
      </div>

      <button
        type="button"
        role="radio"
        aria-checked={look?.kind === "kit"}
        className={card(look?.kind === "kit")}
        onClick={() => onChange({ kind: "kit" })}
      >
        <span className="text-[12.5px] font-semibold text-ink-100">Use my brand kit</span>
        <span className="text-[11.5px] text-ink-300">Your saved logo and colours.</span>
      </button>

      <button
        type="button"
        role="radio"
        aria-checked={look?.kind === "none"}
        className={card(look?.kind === "none")}
        onClick={() => onChange({ kind: "none" })}
      >
        <span className="text-[12.5px] font-semibold text-ink-100">No logo</span>
        <span className="text-[11.5px] text-ink-300">The name is set as a wordmark, in your brand kit's colours.</span>
      </button>
    </div>
  );
};
